import React, { useState } from 'react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon,
  DocumentIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const ContractTable = ({ 
  contracts, 
  loading, 
  pagination, 
  onPageChange, 
  onViewPDF,
  onViewSource 
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpansion = (contractId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(contractId)) {
      newExpanded.delete(contractId);
    } else {
      newExpanded.add(contractId);
    }
    setExpandedRows(newExpanded);
  };

  const formatContributors = (contributors) => {
    if (!contributors || contributors.length === 0) return 'N/A';
    if (contributors.length <= 2) {
      return contributors.join(', ');
    }
    return `${contributors[0]}, ${contributors[1]} (+${contributors.length - 2} more)`;
  };

  const formatWorks = (works) => {
    if (!works || works.length === 0) return 'N/A';
    if (works.length === 1) {
      return `${works[0].workName} (${works[0].workType})`;
    }
    return `${works[0].workName} (${works[0].workType}) (+${works.length - 1} more)`;
  };

  const formatRights = (rights) => {
    if (!rights || rights.length === 0) return 'N/A';
    if (rights.length <= 2) {
      return rights.join(', ');
    }
    return `${rights.slice(0, 2).join(', ')} (+${rights.length - 2} more)`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contract
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Publication
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contributors
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Works
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rights Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contracts.map((contract) => (
              <React.Fragment key={contract._id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <button
                        onClick={() => toggleRowExpansion(contract._id)}
                        className="mr-2 p-1 rounded hover:bg-gray-200"
                      >
                        {expandedRows.has(contract._id) ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Pages {contract.pages}
                        </div>
                        <div className="text-sm text-gray-500">
                          {contract.publicationDate}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{contract.publicationName}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {formatContributors(contract.contributors)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {formatWorks(contract.works)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      contract.rightsLevel?.level === 'A' ? 'bg-green-100 text-green-800' :
                      contract.rightsLevel?.level === 'B' ? 'bg-blue-100 text-blue-800' :
                      contract.rightsLevel?.level === 'C' ? 'bg-yellow-100 text-yellow-800' :
                      contract.rightsLevel?.level === 'D' ? 'bg-orange-100 text-orange-800' :
                      contract.rightsLevel?.level === 'E' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      Level {contract.rightsLevel?.level || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {contract.source?.file_path ? (
                        <span className="truncate max-w-xs block" title={contract.source.file_path}>
                          {contract.source.file_path.split('/').pop()}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {contract.contract_file && (
                        <button
                          onClick={() => onViewPDF(contract)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Contract PDF"
                        >
                          <DocumentIcon className="h-5 w-5" />
                        </button>
                      )}
                      {contract.source && (
                        <button
                          onClick={() => onViewSource(contract)}
                          className="text-green-600 hover:text-green-900"
                          title="View Source Document"
                        >
                          <DocumentDuplicateIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Expanded Row */}
                {expandedRows.has(contract._id) && (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 bg-gray-50">
                      <div className="space-y-4">
                        {/* All Works */}
                        {contract.works && contract.works.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Works:</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {contract.works.map((work, index) => (
                                <div key={index} className="bg-white p-3 rounded border">
                                  <div className="font-medium">{work.workName}</div>
                                  <div className="text-sm text-gray-600">
                                    Type: {work.workType}
                                    {work.workFee && ` • Fee: ${work.workFee}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* All Contributors */}
                        {contract.contributors && contract.contributors.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Contributors:</h4>
                            <div className="flex flex-wrap gap-2">
                              {contract.contributors.map((contributor, index) => (
                                <span
                                  key={index}
                                  className="inline-flex px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                                >
                                  {contributor}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* All Rights */}
                        {contract.rights && contract.rights.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Rights:</h4>
                            <div className="bg-white p-3 rounded border">
                              <ul className="list-disc list-inside space-y-1 text-sm">
                                {contract.rights.map((right, index) => (
                                  <li key={index}>{right}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                        {/* Rights Level Details */}
                        {contract.rightsLevel && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Rights Level Details:</h4>
                            <div className="bg-white p-3 rounded border">
                              <div className="font-medium">Level {contract.rightsLevel.level}</div>
                              <div className="text-sm text-gray-600 mt-1">
                                {contract.rightsLevel.justification}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        {contract.notes && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Notes:</h4>
                            <div className="bg-white p-3 rounded border text-sm">
                              {contract.notes}
                            </div>
                          </div>
                        )}

                        {/* Source Info */}
                        {contract.source && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Source Information:</h4>
                            <div className="bg-white p-3 rounded border text-sm">
                              <div><strong>File Path:</strong> {contract.source.file_path}</div>
                              {contract.contract_file && (
                                <div><strong>Contract File:</strong> {contract.contract_file}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => onPageChange(pagination.current - 1)}
              disabled={pagination.current <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => onPageChange(pagination.current + 1)}
              disabled={pagination.current >= pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {(pagination.current - 1) * pagination.limit + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.current * pagination.limit, pagination.total)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{pagination.total}</span>{' '}
                results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => onPageChange(pagination.current - 1)}
                  disabled={pagination.current <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(
                    pagination.pages - 4,
                    pagination.current - 2
                  )) + i;
                  
                  if (pageNum > pagination.pages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === pagination.current
                          ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => onPageChange(pagination.current + 1)}
                  disabled={pagination.current >= pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractTable;